import type { ColumnDef } from '@tanstack/react-table';
import type { ReactNode } from 'react';

import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

import type { EntitySource } from 'data/entity-sources/types';

import type { DataTableColumnMeta } from './types';

// Text columns longer than this are truncated by default, with the full value shown on hover.
const DEFAULT_TRUNCATE_LENGTH = 50;

function TruncatedText({ value, length }: { value: string; length: number }) {
    return (
        <TooltipProvider>
            <Tooltip>
                <TooltipTrigger asChild>
                    <span>{`${value.slice(0, length)}…`}</span>
                </TooltipTrigger>
                <TooltipContent>{value}</TooltipContent>
            </Tooltip>
        </TooltipProvider>
    );
}

// --- Shared config types ---

type BaseColumnConfig<TData, TFilter> = {
    header: string;
    // Pied de colonne (un total, une moyenne). Il suit la colonne : masquer ou déplacer une colonne
    // déplace son total, là où un pied de tableau écrit à la main se désaligne en silence.
    footer?: ColumnDef<TData, unknown>['footer'];
    filterKey?: keyof TFilter & string;
    defaultVisible?: boolean;
    enableServerSort?: boolean;
} & (
    | { accessorKey: keyof TData & string; accessorFn?: never; id?: string }
    | { accessorFn: (row: TData) => unknown; id: string; accessorKey?: never }
);

function buildAccessorProps<TData>(config: {
    accessorKey?: keyof TData & string;
    accessorFn?: (row: TData) => unknown;
    id?: string;
}) {
    if (config.accessorKey) {
        return {
            accessorKey: config.accessorKey,
            ...(config.id ? { id: config.id } : {}),
        };
    }
    return { accessorFn: config.accessorFn, id: config.id };
}

// --- Text column ---

type TextColumnConfig<TData, TFilter> = BaseColumnConfig<TData, TFilter> & {
    placeholder?: string;
    cell?: ColumnDef<TData, unknown>['cell'];
    // Long values are truncated (with a hover tooltip) by default. Set to false to always show the full text.
    truncate?: boolean;
    truncateLength?: number;
};

export function textColumn<TData, TFilter>(config: TextColumnConfig<TData, TFilter>): ColumnDef<TData, unknown> {
    const meta: DataTableColumnMeta<TFilter> = {
        defaultVisible: config.defaultVisible ?? false,
        enableServerSort: config.enableServerSort ?? false,
    };

    if (config.filterKey) {
        meta.filter = {
            type: 'text',
            filterKey: config.filterKey,
            placeholder: config.placeholder,
        };
    }

    const shouldTruncate = config.truncate ?? true;
    const truncateLength = config.truncateLength ?? DEFAULT_TRUNCATE_LENGTH;
    const defaultCell: ColumnDef<TData, unknown>['cell'] = ({ getValue }) => {
        const rawValue = getValue();
        const value = rawValue == null ? '' : String(rawValue);
        if (shouldTruncate && value.length > truncateLength) {
            return <TruncatedText value={value} length={truncateLength} />;
        }
        return <span>{value}</span>;
    };

    return {
        ...buildAccessorProps<TData>(config),
        header: config.header,
        ...(config.footer ? { footer: config.footer } : {}),
        meta,
        cell: config.cell ?? defaultCell,
    };
}

// --- Boolean column ---

type BooleanColumnConfig<TData, TFilter> = BaseColumnConfig<TData, TFilter> & {
    cell?: ColumnDef<TData, unknown>['cell'];
};

export function booleanColumn<TData, TFilter>(config: BooleanColumnConfig<TData, TFilter>): ColumnDef<TData, unknown> {
    const meta: DataTableColumnMeta<TFilter> = {
        defaultVisible: config.defaultVisible ?? false,
        enableServerSort: config.enableServerSort ?? false,
    };

    if (config.filterKey) {
        meta.filter = {
            type: 'boolean',
            filterKey: config.filterKey,
        };
    }

    return {
        ...buildAccessorProps<TData>(config),
        header: config.header,
        ...(config.footer ? { footer: config.footer } : {}),
        meta,
        cell:
            config.cell ??
            (({ getValue }) => {
                const value = getValue() as boolean | null | undefined;
                return value ? 'Oui' : 'Non';
            }),
    };
}

// --- Multi-select column ---

type MultiSelectColumnConfig<TData, TFilter> = BaseColumnConfig<TData, TFilter> & {
    options: { value: string; label: string }[];
    renderOption?: (option: { value: string; label: string }) => ReactNode;
    cell: ColumnDef<TData, unknown>['cell'];
    defaultFilterValue?: string[];
};

export function multiSelectColumn<TData, TFilter>(
    config: MultiSelectColumnConfig<TData, TFilter>,
): ColumnDef<TData, unknown> {
    const meta: DataTableColumnMeta<TFilter> = {
        defaultVisible: config.defaultVisible ?? false,
        enableServerSort: config.enableServerSort ?? false,
    };

    if (config.filterKey) {
        meta.filter = {
            type: 'multi-select',
            filterKey: config.filterKey,
            options: config.options,
            renderOption: config.renderOption,
            ...(config.defaultFilterValue ? { defaultValue: config.defaultFilterValue } : {}),
        };
    }

    return {
        ...buildAccessorProps<TData>(config),
        header: config.header,
        meta,
        cell: config.cell,
        filterFn: (row, columnId, filterValue: string[]) => {
            if (!filterValue || filterValue.length === 0) return true;
            const cellValue = row.getValue(columnId) as string;
            return filterValue.includes(cellValue);
        },
    };
}

// --- Async multi-select column ---

type AsyncMultiSelectColumnConfig<TData, TFilter> = BaseColumnConfig<TData, TFilter> & {
    loadOptions: (search: string, unresolvedValues: string[]) => Promise<{ value: string; label: string }[]>;
    cell: ColumnDef<TData, unknown>['cell'];
};

/**
 * Comme `multiSelectColumn`, mais les options sont cherchées côté serveur à chaque frappe. À préférer
 * dès que la liste des valeurs possibles peut devenir grande — un référentiel d'utilisateurs, de
 * produits ou de sites ne se charge pas en entier pour cocher une case.
 */
export function asyncMultiSelectColumn<TData, TFilter>(
    config: AsyncMultiSelectColumnConfig<TData, TFilter>,
): ColumnDef<TData, unknown> {
    const meta: DataTableColumnMeta<TFilter> = {
        defaultVisible: config.defaultVisible ?? false,
        enableServerSort: config.enableServerSort ?? false,
    };

    if (config.filterKey) {
        meta.filter = {
            type: 'async-multi-select',
            filterKey: config.filterKey,
            loadOptions: config.loadOptions,
        };
    }

    return {
        ...buildAccessorProps<TData>(config),
        header: config.header,
        meta,
        cell: config.cell,
        enableColumnFilter: false,
    };
}

// --- Entity column (filtre alimenté par une source de collection) ---

type EntityColumnConfig<TData, TFilter> = BaseColumnConfig<TData, TFilter> & {
    source: EntitySource;
    cell?: ColumnDef<TData, unknown>['cell'];
};

/**
 * Colonne dont le filtre est un choix multiple sur une COLLECTION — sites, utilisateurs, fournisseurs.
 *
 * Contrairement à `asyncMultiSelectColumn`, la page n'écrit aucun `loadOptions` : la source porte la
 * recherche serveur, la résolution des libellés d'une sélection venue d'une URL, et le nom de la
 * collection. La configuration reste un objet inerte — c'est le filtre qui appelle le hook.
 *
 * `filterKey` désigne la clé d'IDENTIFIANTS de l'input GraphQL (`siteIds`), pas la colonne affichée
 * (`siteName`) : c'est ce qui permet de filtrer sur un identifiant tout en montrant un nom.
 */
export function entityColumn<TData, TFilter>(config: EntityColumnConfig<TData, TFilter>): ColumnDef<TData, unknown> {
    const meta: DataTableColumnMeta<TFilter> = {
        defaultVisible: config.defaultVisible ?? false,
        enableServerSort: config.enableServerSort ?? false,
    };

    if (config.filterKey) {
        meta.filter = {
            type: 'async-multi-select',
            filterKey: config.filterKey,
            source: config.source,
        };
    }

    return {
        ...buildAccessorProps<TData>(config),
        header: config.header,
        footer: config.footer,
        meta,
        ...(config.cell ? { cell: config.cell } : {}),
        // Le filtre porte sur des identifiants côté serveur : il n'a pas de prédicat client.
        enableColumnFilter: false,
    };
}

// --- Date column ---

type DateColumnConfig<TData, TFilter> = BaseColumnConfig<TData, TFilter> & {
    cell?: ColumnDef<TData, unknown>['cell'];
};

export function dateColumn<TData, TFilter>(config: DateColumnConfig<TData, TFilter>): ColumnDef<TData, unknown> {
    const meta: DataTableColumnMeta<TFilter> = {
        defaultVisible: config.defaultVisible ?? false,
        enableServerSort: config.enableServerSort ?? false,
    };

    if (config.filterKey) {
        meta.filter = {
            type: 'date',
            filterKey: config.filterKey,
        };
    }

    return {
        ...buildAccessorProps<TData>(config),
        header: config.header,
        ...(config.footer ? { footer: config.footer } : {}),
        meta,
        cell:
            config.cell ??
            (({ getValue }) => {
                const value = getValue() as string | number | null | undefined;
                if (!value) return '';
                return new Date(value).toLocaleString('fr-FR');
            }),
    };
}

// --- Date range column ---

type DateRangeColumnConfig<TData, TFilter> = BaseColumnConfig<TData, TFilter> & {
    cell?: ColumnDef<TData, unknown>['cell'];
};

export function dateRangeColumn<TData, TFilter>(
    config: DateRangeColumnConfig<TData, TFilter>,
): ColumnDef<TData, unknown> {
    const meta: DataTableColumnMeta<TFilter> = {
        defaultVisible: config.defaultVisible ?? false,
        enableServerSort: config.enableServerSort ?? false,
    };

    if (config.filterKey) {
        meta.filter = {
            type: 'date-range',
            filterKey: config.filterKey,
        };
    }

    return {
        ...buildAccessorProps<TData>(config),
        header: config.header,
        ...(config.footer ? { footer: config.footer } : {}),
        meta,
        cell:
            config.cell ??
            (({ getValue }) => {
                const value = getValue() as string | number | null | undefined;
                if (!value) return '';
                return new Date(value).toLocaleString('fr-FR');
            }),
    };
}

// --- Number column ---

type NumberColumnConfig<TData, TFilter> = BaseColumnConfig<TData, TFilter> & {
    placeholder?: string;
    cell?: ColumnDef<TData, unknown>['cell'];
};

export function numberColumn<TData, TFilter>(config: NumberColumnConfig<TData, TFilter>): ColumnDef<TData, unknown> {
    const meta: DataTableColumnMeta<TFilter> = {
        defaultVisible: config.defaultVisible ?? false,
        enableServerSort: config.enableServerSort ?? false,
    };

    if (config.filterKey) {
        meta.filter = {
            type: 'number',
            filterKey: config.filterKey,
            placeholder: config.placeholder,
        };
    }

    return {
        ...buildAccessorProps(config),
        header: config.header,
        ...(config.footer ? { footer: config.footer } : {}),
        meta,
        ...(config.cell ? { cell: config.cell } : {}),
    };
}

// --- Number range column ---

type NumberRangeColumnConfig<TData, TFilter> = BaseColumnConfig<TData, TFilter> & {
    cell?: ColumnDef<TData, unknown>['cell'];
};

export function numberRangeColumn<TData, TFilter>(
    config: NumberRangeColumnConfig<TData, TFilter>,
): ColumnDef<TData, unknown> {
    const meta: DataTableColumnMeta<TFilter> = {
        defaultVisible: config.defaultVisible ?? false,
        enableServerSort: config.enableServerSort ?? false,
    };

    if (config.filterKey) {
        meta.filter = {
            type: 'number-range',
            filterKey: config.filterKey,
        };
    }

    return {
        ...buildAccessorProps<TData>(config),
        header: config.header,
        meta,
        ...(config.cell ? { cell: config.cell } : {}),
        filterFn: (row, columnId, filterValue: { gte?: number; lte?: number }) => {
            if (filterValue?.gte == null && filterValue?.lte == null) return true;
            const value = row.getValue<number>(columnId);
            if (typeof value !== 'number' || Number.isNaN(value)) return false;
            if (filterValue.gte != null && value < filterValue.gte) return false;
            if (filterValue.lte != null && value > filterValue.lte) return false;
            return true;
        },
    };
}
